"use client";

/**
 * The Overview's header badges: what this opportunity covers, and what it is
 * evaluating.
 *
 * Extracted from the design source `compass-oppreport.jsx` (`.rv-hdrbadges`,
 * `.rv-hb`, `.rv-hb.link`, `.rv-hbk`, `.rv-hbv`, `.rv-hbarrow`, `.rv-hbpop`,
 * `.rv-bph`, `.rv-bpl`, `.rv-bpattr`, `.rv-bpgo`).
 *
 * This is where the design put deployment scope after it retired the
 * standalone Scope section — a pill, not a card. Both pills always render,
 * because the frame is the point: a reader should see that scope and product
 * are things this report has an opinion about even before either is captured.
 * A missing value reads "Not captured yet" in muted type rather than
 * disappearing or expanding into an empty-state box.
 *
 * The design opens each pill's disclosure on `:hover`, which no keyboard can
 * reach. Here the pill is a `<button>` and the popover is click-toggled,
 * dismissed on Escape (focus returns to the trigger) and on an outside click —
 * the hand-rolled idiom `ConfirmGoBackModal` uses, since `packages/ui` has no
 * shared Dialog primitive. The product pill's navigate-to-rationale action
 * moves into the popover's footer, so clicking the pill discloses rather than
 * navigates.
 */

import {
  type CSSProperties,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { ArrowR } from "../lib/icons";
import { formatProductBadge, type ProductIdentity } from "./product-identity";
import type { ReportScope, ReportScopeTile } from "./report-types";

export interface ReportHeaderBadgesProps {
  scope: ReportScope;
  /** `null` ⇒ the pill renders its placeholder. */
  product: ProductIdentity | null;
  /** Scrolls to the Product selection section, as the design's pill does. */
  onViewProduct?: () => void;
}

/** Design copy has no equivalent — every badge in the design carries a value. */
const PLACEHOLDER = "Not captured yet";

/**
 * `.rv-hb` — the pill itself.
 *
 * `max-w-full` + `min-w-0` are not in the design, which only ever renders
 * short descriptors. Intake values are free text, so the pill needs a ceiling:
 * without one a single long fact stretches it across the column and wraps the
 * header. The mapper keeps prose off the line (`SCOPE_TILE_CURATION`); this is
 * the layout guarantee behind it, so no value can break the header.
 */
const PILL = [
  "group relative inline-flex items-center gap-2 min-h-[30px] box-border",
  "font-[family-name:var(--font-body)] text-left max-w-full min-w-0",
  "bg-(--bg-hover) border border-(--border-subtle) rounded-full px-[13px]",
].join(" ");

/** `.rv-hb.link` — the interactive pill's hover treatment. */
const PILL_INTERACTIVE =
  "cursor-pointer transition-colors duration-300 ease-(--ease) hover:border-(--fg-brand) hover:bg-(--bg-sky)";

/** `.rv-hbk` — the 10px uppercase key. Never truncates; the value yields first. */
const PILL_KEY =
  "shrink-0 text-[10px] leading-none tracking-[0.08em] uppercase font-semibold text-(--fg-muted)";

/**
 * `.rv-hbv` — the 12.5px value, truncated to one line.
 *
 * The design's `line-height:1` is relaxed to `1.3` here purely to give
 * descenders room inside `truncate`'s `overflow: hidden` — the pill is
 * `min-h-[30px]` and centres its content, so the rendered position is
 * unchanged; only the clipping box grows.
 */
const PILL_VALUE = "min-w-0 truncate text-[12.5px] leading-[1.3] font-semibold";

/** `.rv-hbpop` — the disclosure panel. */
/**
 * `.rv-hbpop` — the disclosure panel.
 *
 * The design pins a 300px panel to the trigger's left edge. That runs off the
 * viewport for a pill sitting near the right edge — the Product pill on a
 * narrow screen — so the width is clamped to the viewport and the alignment
 * flips to the trigger's right edge when there is not room to open leftward.
 * `--rv-hbpop-w` keeps the two in step: the flip measures against the same
 * width the panel renders at.
 */
const POPOVER = [
  "absolute top-[calc(100%+7px)] z-[80] flex flex-col gap-[5px]",
  "w-[var(--rv-hbpop-w)] max-w-[calc(100vw-2rem)]",
  "px-[14px] py-3 font-[family-name:var(--font-body)]",
  "bg-(--bg-white) border border-(--border-default)",
  "rounded-(--radius-container) shadow-(--shadow-md)",
].join(" ");

/** The design's panel width, and the basis for the flip measurement. */
const POPOVER_WIDTH_PX = 300;

/** Gap kept between the panel and the viewport edge when deciding to flip. */
const POPOVER_VIEWPORT_MARGIN_PX = 16;

/**
 * `.rv-bph` — a section head. The arbitrary variant is the design's
 * `.rv-bph:not(:first-child){margin-top:5px}` verbatim, so whichever section
 * happens to be first (any of them can be omitted) carries no top gap.
 */
const POPOVER_HEAD = [
  "text-[9.5px] tracking-[0.09em] uppercase font-semibold text-(--fg-muted)",
  "[&:not(:first-child)]:mt-[5px]",
].join(" ");

/** `.rv-bpl` — one line of popover body copy. */
const POPOVER_LINE = "text-[12px] leading-[1.5] text-(--fg-secondary)";

/** `.rv-bpattr` — the attribution footer. */
const POPOVER_ATTRIBUTION = [
  "mt-[7px] pt-2 border-t border-(--border-subtle)",
  "text-[11px] text-(--fg-subtle)",
].join(" ");

/**
 * `.rv-bpgo` — the footer action. Sized for a `<button>`: preflight already
 * zeroes a button's border width and padding, so the top rule and the top
 * padding are the only box properties needed and nothing has to be reset.
 */
const POPOVER_ACTION = [
  "mt-[7px] pt-2 border-t border-(--border-subtle) bg-transparent",
  "flex w-full cursor-pointer items-center gap-[5px] text-left",
  "text-[11px] font-semibold text-(--fg-brand)",
].join(" ");

function pillValueClass(present: boolean): string {
  return present
    ? `${PILL_VALUE} text-(--fg-primary)`
    : `${PILL_VALUE} font-medium text-(--fg-muted)`;
}

/** A value that means nothing without its label — "200", "1,400". */
function isQuantitative(value: string): boolean {
  return /\d/.test(value) && !/[a-z]/i.test(value);
}

/**
 * The design's pill reads "200 inpatient beds · 400 nurses" — each scoped
 * fact as its value followed by its own label, which is how the tiles carry
 * them ("Inpatient beds" / "200").
 *
 * A bare number needs that label to mean anything; a captured phrase does not,
 * and appending its category makes it worse — real intake facts are
 * `{ label: "Function / department", value: "Nursing" }`, which the design's
 * rule renders "Nursing function / department". So a quantitative tile keeps
 * the design's `value label` form and a qualitative one states its value
 * alone, giving "Inpatient hospital · Nursing".
 *
 * Only the curated tiles reach this line (see `ReportScopeTile.inPill`); the
 * popover carries the rest.
 *
 * Captured-but-uncurated is not the same as uncaptured. Intake can leave every
 * tile off the line — an unallowlisted label, or a `deployment_context.scope`
 * value past the length budget — and "Not captured yet" over a popover that
 * lists real scope is the one reading that is simply false.
 */
function formatScope(scope: ReportScope): string | null {
  const pillTiles = scope.tiles.filter((tile) => tile.inPill);
  if (pillTiles.length > 0) {
    return pillTiles.map(scopeTileSentence).join(" · ");
  }
  return scope.tiles.length > 0 ? SCOPE_DISCLOSED : null;
}

/** What the pill says when scope was captured but none of it fits the line. */
const SCOPE_DISCLOSED = "See details";

/**
 * How one tile reads on the pill line.
 *
 * Exported because the mapper's whole-line length budget has to measure the
 * string this produces, not the raw value: a quantitative tile expands from
 * `"200"` to `"200 scope"`, so budgeting the raw value lets the rendered line
 * overrun. One renderer, one measurement — the two cannot drift.
 */
export function scopeTileSentence(tile: ReportScopeTile): string {
  return isQuantitative(tile.value)
    ? `${tile.value} ${tile.label.toLowerCase()}`
    : tile.value;
}

/** One popover line: "Care setting: Inpatient hospital". */
function scopeTileLine(tile: ReportScopeTile): string {
  return `${tile.label}: ${tile.value}`;
}

/** The design's pill reads "Aiva Health · Ambient nursing" — vendor, product. */
function formatProduct(product: ProductIdentity | null): string | null {
  if (!product) return null;
  return formatProductBadge(product) || null;
}

/**
 * A pill that discloses a popover on click.
 *
 * `popover` renders the body and is handed a `close` so a footer action can
 * dismiss the disclosure it lives in. `null` degrades the pill to a plain,
 * non-interactive `<span>` — an empty popover is worse than none, and the
 * design has no state for one.
 */
function BadgePill({
  label,
  value,
  arrow,
  popoverLabel,
  popover,
  open,
  onOpenChange,
}: {
  label: string;
  value: string | null;
  /** `.rv-hbarrow` — the design gives it to the product pill only. */
  arrow?: boolean;
  popoverLabel: string;
  popover: ((close: () => void) => React.ReactNode) | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  /**
   * Which edge the panel is pinned to.
   *
   * Decided when it opens rather than on every render: the trigger has not
   * moved between the click and the paint, and re-measuring on scroll would
   * make the panel jump under the reader.
   */
  const [alignEnd, setAlignEnd] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const popoverId = useId();

  const openPopover = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      // Flip only when opening leftward would overrun the viewport, so the
      // design's left alignment stays the default everywhere it fits.
      setAlignEnd(
        rect.left + POPOVER_WIDTH_PX >
          window.innerWidth - POPOVER_VIEWPORT_MARGIN_PX,
      );
    }
    onOpenChange(!open);
  }, [onOpenChange, open]);

  /**
   * Move focus into the panel when it opens.
   *
   * Its body is static text, so without this a keyboard or screen-reader
   * reader tabs from the trigger straight past everything the disclosure
   * exists to say. The panel itself is the focus target rather than a control
   * inside it, because on the Scope pill there is no control to focus.
   */
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      onOpenChange(false);
      triggerRef.current?.focus();
    };
    // `mousedown`, not `click`: a click that starts inside the popover and
    // ends outside it (a drag over a text selection) must not dismiss.
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && containerRef.current?.contains(target)) {
        return;
      }
      onOpenChange(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onPointerDown);
    };
  }, [onOpenChange, open]);

  const body = (
    <>
      <span className={PILL_KEY}>{label}</span>
      <span className={pillValueClass(value !== null)}>
        {value ?? PLACEHOLDER}
      </span>
      {arrow && (
        // `.rv-hbarrow`
        <span className="inline-flex shrink-0 text-(--fg-subtle) transition-[color,transform] duration-300 ease-(--ease) group-hover:translate-x-0.5 group-hover:text-(--fg-brand)">
          <ArrowR size={12} />
        </span>
      )}
    </>
  );

  if (!popover) return <span className={PILL}>{body}</span>;

  return (
    <div ref={containerRef} className="relative min-w-0 max-w-full">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? popoverId : undefined}
        className={`${PILL} ${PILL_INTERACTIVE}`}
        onClick={openPopover}
      >
        {body}
      </button>
      {open && (
        <div
          ref={panelRef}
          id={popoverId}
          role="dialog"
          aria-label={popoverLabel}
          tabIndex={-1}
          className={`${POPOVER} ${alignEnd ? "right-0" : "left-0"} outline-none`}
          style={{ "--rv-hbpop-w": `${POPOVER_WIDTH_PX}px` } as CSSProperties}
        >
          {/* No refocus here, unlike Escape: the product pill's action
              navigates to another section, and pulling focus back to the pill
              would scroll it straight back. */}
          {popover(() => onOpenChange(false))}
        </div>
      )}
    </div>
  );
}

export function ReportHeaderBadges({
  scope,
  product,
  onViewProduct,
}: ReportHeaderBadgesProps) {
  const [activePopover, setActivePopover] = useState<
    "scope" | "product" | null
  >(null);
  const scopeValue = formatScope(scope);
  const productValue = formatProduct(product);

  const deploymentTiles = scope.tiles.filter(
    (tile) => tile.origin === "deployment",
  );
  const organizationTiles = scope.tiles.filter(
    (tile) => tile.origin === "organization",
  );
  /** Sections with no data are omitted, not stubbed — so an empty popover
   *  never opens. */
  const hasScopePopover =
    scope.tiles.length > 0 || scope.outOfScope !== null || !!scope.attribution;

  return (
    // `.rv-hdrbadges`
    <div className="mb-4 flex flex-wrap gap-2">
      <BadgePill
        label="Scope"
        value={scopeValue}
        popoverLabel="What this report covers"
        open={activePopover === "scope"}
        onOpenChange={(open) => setActivePopover(open ? "scope" : null)}
        popover={
          hasScopePopover
            ? () => (
                <>
                  {deploymentTiles.length > 0 && (
                    <>
                      <span className={POPOVER_HEAD}>What this covers</span>
                      {deploymentTiles.map((tile) => (
                        <span key={tile.id} className={POPOVER_LINE}>
                          {scopeTileLine(tile)}
                        </span>
                      ))}
                    </>
                  )}
                  {organizationTiles.length > 0 && (
                    <>
                      {/* Never presented as this deployment's scope: intake
                          captures no numeric scope at all, so these are the
                          organization's own counts and say so. */}
                      <span className={POPOVER_HEAD}>Organization-wide</span>
                      {organizationTiles.map((tile) => (
                        <span key={tile.id} className={POPOVER_LINE}>
                          {scopeTileLine(tile)}
                        </span>
                      ))}
                      <span className={POPOVER_LINE}>
                        These are system-wide totals, not counts scoped to this
                        deployment.
                      </span>
                    </>
                  )}
                  {scope.outOfScope && (
                    <>
                      <span className={POPOVER_HEAD}>Out of scope</span>
                      <span className={POPOVER_LINE}>{scope.outOfScope}</span>
                    </>
                  )}
                  {scope.attribution && (
                    // `.rv-bpattr`
                    <span className={POPOVER_ATTRIBUTION}>
                      Captured at intake by {scope.attribution.capturedBy} ·{" "}
                      {scope.attribution.capturedAt}
                    </span>
                  )}
                </>
              )
            : null
        }
      />

      <BadgePill
        label="Product"
        value={productValue}
        arrow={!!onViewProduct}
        popoverLabel="Why this product"
        open={activePopover === "product"}
        onOpenChange={(open) => setActivePopover(open ? "product" : null)}
        popover={
          onViewProduct
            ? (close) => (
                // The design also carries "Why this product" rationale rows
                // and a "Measured against" comparables line here. Neither is
                // modelled yet (§5 gaps #11, #12), and a section with no data
                // is omitted rather than stubbed — so today the footer action
                // stands alone. `.rv-bpgo`
                <button
                  type="button"
                  className={POPOVER_ACTION}
                  onClick={() => {
                    close();
                    onViewProduct();
                  }}
                >
                  <ArrowR size={11} />
                  See full rationale
                </button>
              )
            : null
        }
      />
    </div>
  );
}
